import "./CulturesContainer.less";
import * as React from "react";
import {
    AppScope,
    Client,
    ContentStore,
    Culture,
    CultureStore,
    GlobalStores,
    ScopedOrganizationStore,
    UrlFormatter,
} from "@atman/business";
import { AtBadge, AtButton, AtLink, IAtButtonProps } from "@atman/design-system";
import { AtListHeader } from "../../components/AtListHeader";
import { AtPageHeader } from "../../components/AtPageHeader";
import { BaseForm } from "../../components/BaseForm";
import { CultureCard } from "./components/CultureCard";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IRoutedAppContext, withRoutedAppContext } from "../../contexts";
import { autorun, computed, observable } from "mobx";
import { inject, observer } from "mobx-react";

export interface ICulturesContainerProps extends IRoutedAppContext {
    cultureStore?: CultureStore;
    scopedOrganizationStore?: ScopedOrganizationStore;
    contentStore?: ContentStore;
}

@inject(GlobalStores.cultureStore, GlobalStores.scopedOrganizationStore, GlobalStores.contentStore)
@observer
class CulturesContainerImpl extends BaseForm<ICulturesContainerProps, {}> {
    @observable private searchString: string = "";
    @observable private filteredCultures: Culture[] = [];
    @observable private currentOrgCulture: Culture | undefined;

    constructor(props) {
        super(props);

        autorun(() => {
            this.filteredCultures = this._filteredCultures;
            this.currentOrgCulture = this._currentOrgCulture;
        });
    }

    @computed private get _currentOrgCulture(): Culture | undefined {
        const { cultureStore, scopedOrganizationStore } = this.props;

        const cultureId = (scopedOrganizationStore!.scopedOrganization as Client)?.cultureId;

        if (!cultureId) {
            return;
        }

        return cultureStore!.findItemById(cultureId);
    }

    @computed private get _filteredCultures(): Culture[] {
        const { cultureStore } = this.props;

        if (!this.searchString) {
            return cultureStore!.items;
        }

        return cultureStore!.items.filter(
            (x) =>
                x.name?.toLowerCase().includes(this.searchString.toLowerCase()) ||
                x.description?.toLowerCase().includes(this.searchString.toLowerCase()),
        );
    }

    private personalizeOrgCulture = () => {
        const { history } = this.props;

        history.push(UrlFormatter.formatReactPath("/Settings/Cultures/Create"), { forOrgCulture: true });
    };

    private editCurrentOrgCulture = () => {
        const { history } = this.props;

        if (!this.currentOrgCulture) {
            console.warn("No org culture currently assigned");
            return;
        }

        history.push(UrlFormatter.formatReactPath(`/Settings/Cultures/${this.currentOrgCulture!.id}`));
    };

    render() {
        const { history, scope, contentStore } = this.props;

        const buttons: IAtButtonProps[] = [
            {
                onClick: () => history.push(UrlFormatter.formatReactPath("/Settings/Cultures/Create")),
                children: "global.createCulture".localize(),
            },
        ];

        const department = this.currentOrgCulture?.departmentId
            ? contentStore!.getDepartmentById(this.currentOrgCulture.departmentId)
            : undefined;

        return (
            <div id="CulturesContainer">
                {scope === AppScope.Client && (
                    <div className="org-culture-section">
                        <div className="org-culture-container">
                            <h2 className={"org-culture-title"}>{"global.organizationCulture".localize()}</h2>
                            <div className={"org-culture-desc"}>
                                {"global.organizationCulture.description".localize()}
                            </div>
                            {!this.currentOrgCulture ? (
                                <div className="org-culture-content unassigned">
                                    <div className="left-section">
                                        <div className="icon-container">
                                            <FontAwesomeIcon icon={["far", "theater-masks"]} />
                                        </div>
                                        <div className="unassigned-org-label">
                                            {"global.noCultureAssigned".localize()}
                                        </div>
                                    </div>
                                    <div className="right-section">
                                        <AtLink icon={["far", "circle"]} onClick={this.personalizeOrgCulture}>
                                            {"global.personalizeYourCulture".localize()}
                                        </AtLink>
                                    </div>
                                </div>
                            ) : (
                                <div className="org-culture-content">
                                    <div className="left-section">
                                        <div className="info-container">
                                            <div className="subtitle">{department?.name}</div>
                                            <h2 className="title">{this.currentOrgCulture.name}</h2>
                                        </div>
                                        <div className="badge-container">
                                            <AtBadge size={"sm"}>{`${
                                                this.currentOrgCulture.competencyIds.length
                                            } ${"global.competencies".localize()}`}</AtBadge>
                                        </div>
                                    </div>
                                    <div className="right-section">
                                        <AtButton color={"secondary"} onClick={this.editCurrentOrgCulture}>
                                            {"global.edit".localize()}
                                        </AtButton>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
                <div className="cultures-list-section">
                    <AtPageHeader
                        label={"global.culturesList".localize()}
                        description={"global.culturesList.description".localize()}
                        helpCenterArticleId={"360054192232"}
                        helpCenterUrlLabel={"global.learnMoreAboutTheDifferentCultures".localize()}
                    />
                    <AtListHeader
                        searchString={this.searchString}
                        onSearchStringChange={this.onTextFieldChange}
                        buttons={buttons}
                        searchInputPlaceholder={"global.searchByNameOrDescription".localize()}
                    />
                    <div className="cards-container">
                        {this.filteredCultures.map((x) => (
                            <CultureCard culture={x} key={x.id} />
                        ))}
                    </div>
                </div>
            </div>
        );
    }
}

export const CulturesContainer = withRoutedAppContext(CulturesContainerImpl);
